import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Route, Switch, Redirect } from 'react-router-dom';

import PrivateRoute from 'components/utility/PrivateRoute/PrivateRoute';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import SyncTopicFiltersStoreWithURL from 'components/utility/SyncTopicFiltersStoreWithURL';

import Home from 'pages/Home/Home';
import Login from 'pages/Login';
import ErrorPage from 'pages/ErrorPage';

import Entity from './pages/Entity';
import EntityCompare from './pages/EntityCompare';
import Search from './pages/Search';
import Glossary from './pages/Glossary';
import GlossaryModal from './pages/GlossaryModal';
import {
  shouldLoadTopicFilters,
  loadTopicFilters,
} from 'redux/reducers/topicFilters';
import { loadConsulSettings } from 'redux/reducers/consul_parameters';

import gtmExists from './helpers/gtmExists';

import topics from 'topics';
import CustomList from 'pages/CustomList';
import { IAuthenticationProfile } from 'redux/reducers/authentication';
import { RootState } from 'redux/rootReducer';
import { ITopicFilterState } from 'components/TopicFilters/shared';
import CustomListInvitation from 'pages/CustomList/CustomListInvitation';
import Chart from 'pages/Charts';
import TopLists from 'pages/TopLists';
import PlaylistActivity from 'pages/PlaylistActivity';
import FAQActivityTracker from 'pages/FAQActivityTracker';
import DownloadOpusNotifierAppPage from 'pages/DownloadOpusNotifierApp';
import PreUnsubscribedPage from 'pages/PreUnsubscribePage/PreUnsubscribedPage';
import EntityCompareIntermediate from './pages/EntityCompareIntermediate';
import FAQ from 'pages/FAQ/FAQ';
import UpdatesModal from 'components/UpdatesModal';

import { checkAuthenticationStatus } from 'redux/reducers/authentication';
import SystemStatus from 'pages/SystemStatus/SystemStatus';
import UserProfileOnboarding from 'components/UserProfileOnboarding';
import Cookies from 'js-cookie';
import { logLoginEvent, logToAmplitude } from 'helpers/logAmplitudeEvent';
import TrainingVideo from 'pages/TrainingVideo';
import {
  loadOrInitUserSettings,
  shouldLoadUserSettings,
} from 'redux/reducers/userSettings';
import mapPageToAmplitudeName from 'helpers/mapPageToAmplitudeName';
import Help from 'pages/Help/Help';
import checkBetaGroup from 'helpers/checkBetaGroup';
import getRuntimeEnv from 'helpers/env';

interface IAppRoutesProps {
  location: any;
  history: any;
  shouldLoadTopicFilters: Function;
  loadTopicFilters: Function;
  loadConsulSettings: Function;
  isExternalUser: boolean;
  topicFiltersState: ITopicFilterState;
  authenticationProfile: IAuthenticationProfile;
  userLocation: string | null;
  userDivision: string | null;
  userRole: string | null;
  loadOrInitUserSettings: Function;
  shouldLoadUserSettings: Function;
  userHasVisitedVideoPage: boolean;
  loadedAllUserSettings: boolean;
  isOpusNotifierOn?: boolean;
  BetaGroupPriorityPlaylist?: string[];
}

const logPageViewEvent = (location: any): void => {
  const locationPathArray = location.pathname.split('/');
  const subPage = locationPathArray.pop();

  const mainPage =
    locationPathArray.length > 1
      ? locationPathArray.splice(-1, 1).pop()
      : undefined;

  logToAmplitude('PageView', {
    ...location,
    pageName: mapPageToAmplitudeName(subPage, mainPage),
  });
};

class AppRoutes extends Component<
  IAppRoutesProps,
  { showOnboarding: boolean | null; showOpusNotifier: boolean }
> {
  constructor(props: IAppRoutesProps) {
    super(props);
    const showOpusNotifier =
      props.isOpusNotifierOn &&
      checkBetaGroup(
        props.BetaGroupPriorityPlaylist,
        props.authenticationProfile?.email
      );
    this.state = {
      showOnboarding: null,
      showOpusNotifier: showOpusNotifier || false,
    };
  }

  previousLocation: any = this.props.location;

  componentDidMount(): void {
    if (this.props.shouldLoadTopicFilters()) {
      this.props.loadTopicFilters();
    }

    // Save some settings to the datalayer for GTM
    if (gtmExists()) {
      (window as any).dataLayer.push({
        isExternalUser: this.props.isExternalUser,
      });
    }

    this.props.loadConsulSettings();

    if (
      this.props.authenticationProfile &&
      this.props.shouldLoadUserSettings()
    ) {
      this.props.loadOrInitUserSettings();
    }

    logPageViewEvent(this.props.location);
  }

  componentDidUpdate(prevProps: IAppRoutesProps): void {
    // Custom GTM event on path change
    if (prevProps.location.pathname !== this.props.location.pathname) {
      if (window && (window as any).dataLayer) {
        (window as any).dataLayer.push({
          event: 'Pageview',
          pagePath: this.props.location.pathname,
          queryParameters: this.props.location.search,
        });

        logPageViewEvent(this.props.location);
      }
    }

    const amplitudeInitCookie = Cookies.get('InitAmplitude');

    const showOnboarding =
      this.state.showOnboarding === null
        ? !this.props.userLocation ||
          !this.props.userDivision ||
          !this.props.userRole
        : this.state.showOnboarding;

    if (
      getRuntimeEnv().REACT_APP_AMPLITUDE_KEY &&
      amplitudeInitCookie &&
      amplitudeInitCookie === '1' &&
      this.props.loadedAllUserSettings &&
      this.props.authenticationProfile &&
      !showOnboarding
    ) {
      logLoginEvent(
        {
          user_has_visited_video_page: this.props.userHasVisitedVideoPage,
          professional_role: this.props.userRole,
          working_location: this.props.userLocation,
          label_or_department: this.props.userDivision,
        },
        this.props.authenticationProfile
      );
    }
  }

  UNSAFE_componentWillUpdate(nextProps: IAppRoutesProps): void {
    const { location } = this.props;

    // set previousLocation if props.location is not modal
    if (
      nextProps.history.action !== 'POP' &&
      (!location.state || !location.state.modal)
    ) {
      this.previousLocation = this.props.location;
    }
  }

  /**
   * @returns
   * @memberof AppRoutes
   */
  render(): any {
    const {
      location,
      topicFiltersState,
      authenticationProfile,
      loadedAllUserSettings,
      isExternalUser,
      userLocation,
      userDivision,
      userRole,
    } = this.props;

    let isModalActive = false;
    let modalOptions = {};

    if (location.state && typeof location.state === 'object') {
      if ('modal' in location.state) {
        isModalActive =
          JSON.stringify(this.previousLocation) !== JSON.stringify(location);
        modalOptions = location.state.modal;
      }
    }

    const switchLocation = isModalActive ? this.previousLocation : location;
    const entityTypes = 'track|release|artist|project'; // Available entity type routes without "label" -> it has only one page
    const topicTypes = topics.join('|');
    const { isAuthenticated } = checkAuthenticationStatus();

    const showUserProfileOnboarding =
      !isExternalUser &&
      loadedAllUserSettings &&
      this.state.showOnboarding === null
        ? !userLocation || !userDivision || !userRole
        : this.state.showOnboarding;

    /**
     * If all goes well after the (optional) Redirect we end up
     * in the normal router configuration
     */
    return (
      <ErrorBoundary>
        {authenticationProfile && (
          <SyncTopicFiltersStoreWithURL topicFiltersState={topicFiltersState} />
        )}

        {isAuthenticated && <UpdatesModal history={this.props.history} />}

        <Switch location={switchLocation}>
          <Route
            exact
            path="/authenticate"
            render={(renderProps: any): any => <Login {...renderProps} />}
          />
          <PrivateRoute
            exact
            path="/"
            render={(renderProps: any): any => <Home {...renderProps} />}
          />
          {/** The entry page for an entity page
           * example:
           * /entity/track/GRP-9289831/streaming
           **/}
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/`}
            to={`/entity/:entityType(${entityTypes})/:entityId/performance/streaming`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/overview`}
            to={`/entity/:entityType(${entityTypes})/:entityId/overview/summary`}
          />

          {/** Redirects for the nested performance topics **/}
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/performance`}
            to={`/entity/:entityType(${entityTypes})/:entityId/performance/all`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/streaming`}
            to={`/entity/:entityType(${entityTypes})/:entityId/performance/streaming`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/playlist`}
            to={`/entity/:entityType(${entityTypes})/:entityId/performance/playlist`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/download`}
            to={`/entity/:entityType(${entityTypes})/:entityId/performance/download`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/physical`}
            to={`/entity/:entityType(${entityTypes})/:entityId/performance/physical`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/radio`}
            to={`/entity/:entityType(${entityTypes})/:entityId/airplay/radio`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/airplay`}
            to={`/entity/:entityType(${entityTypes})/:entityId/airplay/radio`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/performance`}
            to={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/performance/all`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/streaming`}
            to={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/performance/streaming`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/download`}
            to={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/performance/download`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/physical`}
            to={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/performance/physical`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/radio`}
            to={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/airplay/radio`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/airplay`}
            to={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/airplay/radio`}
          />
          <Redirect
            exact
            from={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/performance`}
            to={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/performance/all`}
          />
          <Redirect
            exact
            from={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/streaming`}
            to={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/performance/streaming`}
          />
          <Redirect
            exact
            from={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/download`}
            to={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/performance/download`}
          />
          <Redirect
            exact
            from={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/playlist`}
            to={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/performance/playlist`}
          />
          <Redirect
            exact
            from={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/physical`}
            to={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/performance/physical`}
          />
          <Redirect
            exact
            from={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/radio`}
            to={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/airplay/radio`}
          />
          <Redirect
            exact
            from={`/compare/entity/:entityType(${entityTypes})/:entityIds/performance`}
            to={`/compare/entity/:entityType(${entityTypes})/:entityIds/performance/all`}
          />
          <Redirect
            exact
            from={`/compare/entity/:entityType(${entityTypes})/:entityIds/streaming`}
            to={`/compare/entity/:entityType(${entityTypes})/:entityIds/performance/streaming`}
          />
          <Redirect
            exact
            from={`/compare/entity/:entityType(${entityTypes})/:entityIds/playlist`}
            to={`/compare/entity/:entityType(${entityTypes})/:entityIds/performance/playlist`}
          />
          <Redirect
            exact
            from={`/compare/entity/:entityType(${entityTypes})/:entityIds/download`}
            to={`/compare/entity/:entityType(${entityTypes})/:entityIds/performance/download`}
          />
          <Redirect
            exact
            from={`/compare/entity/:entityType(${entityTypes})/:entityIds/physical`}
            to={`/compare/entity/:entityType(${entityTypes})/:entityIds/performance/physical`}
          />
          <Redirect
            exact
            from={`/compare/entity/:entityType(${entityTypes})/:entityIds/radio`}
            to={`/compare/entity/:entityType(${entityTypes})/:entityIds/airplay/radio`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/social`}
            to={`/entity/:entityType(${entityTypes})/:entityId/social/engagement`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/social`}
            to={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/social/engagement`}
          />
          <Redirect
            exact
            from={`/compare/entity/:entityType(${entityTypes})/:entityIds/social`}
            to={`/compare/entity/:entityType(${entityTypes})/:entityIds/social/engagement`}
          />
          {/** Redirects for the nested chart topics **/}
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:entityId/charts`}
            to={`/entity/:entityType(${entityTypes})/:entityId/charts/dsp`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/charts`}
            to={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/charts/dsp`}
          />
          <Redirect
            exact
            from={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/charts`}
            to={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/charts/dsp`}
          />
          <Redirect
            exact
            from={`/compare/entity/:entityType(${entityTypes})/:entityIds/charts`}
            to={`/compare/entity/:entityType(${entityTypes})/:entityIds/charts/dsp`}
          />
          <PrivateRoute
            path={`/entity/:entityType(${entityTypes})/:entityId/:topic(${topicTypes})/:subTopic`}
            render={(renderProps: any): any => <Entity {...renderProps} />}
          />
          <PrivateRoute
            path={`/entity/:entityType(${entityTypes})/:entityId/:topic(${topicTypes})`}
            render={(renderProps: any): any => <Entity {...renderProps} />}
          />
          <PrivateRoute
            path={`/entity/:entityType(label)/:entityId`}
            render={(renderProps: any): any => <Entity {...renderProps} />}
          />
          {/** Routes for intermediate entities compare page */}
          <PrivateRoute
            path={`/compare/all/entity/:entityType(${entityTypes})/:entityId/:topic(${topicTypes})/:subTopic`}
            render={(renderProps: any): any => (
              <EntityCompareIntermediate
                {...renderProps}
                compareMode={'entity'}
              />
            )}
          />
          <PrivateRoute
            path={`/compare/all/entity/:entityType(${entityTypes})/:topic(${topicTypes})/:subTopic`}
            render={(renderProps: any): any => (
              <EntityCompareIntermediate
                {...renderProps}
                compareMode={'entity'}
              />
            )}
          />
          <PrivateRoute
            path={`/compare/all/entity/:entityType(${entityTypes})/:topic(${topicTypes})`}
            render={(renderProps: any): any => (
              <EntityCompareIntermediate
                {...renderProps}
                compareMode={'entity'}
              />
            )}
          />
          <PrivateRoute
            path={`/compare/all/entity/:entityType(${entityTypes})`}
            render={(renderProps: any): any => (
              <EntityCompareIntermediate
                {...renderProps}
                compareMode={'entity'}
              />
            )}
          />
          {/** Routes for intermediate countries compare page */}
          <PrivateRoute
            path={`/compare/all/country/entity/:entityType(${entityTypes})/:entityId/:topic(${topicTypes})/:subTopic`}
            render={(renderProps: any): any => (
              <EntityCompareIntermediate
                {...renderProps}
                compareMode={'country'}
              />
            )}
          />
          <PrivateRoute
            path={`/compare/all/country/entity/:entityType(${entityTypes})/:topic(${topicTypes})/:subTopic`}
            render={(renderProps: any): any => (
              <EntityCompareIntermediate
                {...renderProps}
                compareMode={'country'}
              />
            )}
          />
          <PrivateRoute
            path={`/compare/all/country/entity/:entityType(${entityTypes})/:topic(${topicTypes})`}
            render={(renderProps: any): any => (
              <EntityCompareIntermediate
                {...renderProps}
                compareMode={'country'}
              />
            )}
          />
          <PrivateRoute
            path={`/compare/all/country/entity/:entityType(${entityTypes})`}
            render={(renderProps: any): any => (
              <EntityCompareIntermediate
                {...renderProps}
                compareMode={'country'}
              />
            )}
          />
          {/** The entry page for a version entity, which is a version of another (a group) entity
           * example:
           * /entity/track/GRP-9289831/version/ISRC9289831/overview
           **/}
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/`}
            to={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/performance/streaming`}
          />
          <Redirect
            exact
            from={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/overview`}
            to={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/overview/summary`}
          />

          <PrivateRoute
            path={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/:topic(${topicTypes})/:subTopic`}
            render={(renderProps: any): any => <Entity {...renderProps} />}
          />
          <PrivateRoute
            path={`/entity/:entityType(${entityTypes})/:parentEntityId/version/:entityId/:topic(${topicTypes})`}
            render={(renderProps: any): any => <Entity {...renderProps} />}
          />

          {/** The compare country page for any type of entity
           * example for a track:
           * /compare/country/US,UK/entity/track/GRP-9289831/streaming
           * example for a version track:
           * /compare/country/US,UK/entity/track/GRP-9289831/version/ISRC9156216/streaming
           **/}
          <Redirect
            exact
            from={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/`}
            to={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/performance/streaming`}
          />
          <Redirect
            exact
            from={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/overview`}
            to={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/overview/summary`}
          />
          <PrivateRoute
            path={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/:topic(${topicTypes})/:subTopic`}
            render={(renderProps: any): any => (
              <Entity {...renderProps} compareMode={'country'} />
            )}
          />
          <PrivateRoute
            path={`/compare/country/:countries/entity/:entityType(${entityTypes})/:entityId/:topic(${topicTypes})`}
            render={(renderProps: any): any => (
              <Entity {...renderProps} compareMode={'country'} />
            )}
          />
          {/** The entry page for comparing entities, should should be of the same type but can be groups or versions.
           * example:
           * /compare/entity/track/GRP-9289831,ISRC9289831/overview
           **/}
          <Redirect
            exact
            from={`/compare/entity/:entityType(${entityTypes})/:entityIds/`}
            to={`/compare/entity/:entityType(${entityTypes})/:entityIds/performance/streaming`}
          />
          <Redirect
            exact
            from={`/compare/entity/:entityType(${entityTypes})/:entityIds/overview`}
            to={`/compare/entity/:entityType(${entityTypes})/:entityIds/overview/summary`}
          />
          <PrivateRoute
            path={`/compare/entity/:entityType(${entityTypes})/:entityIds/:topic(${topicTypes})/:subTopic`}
            render={(renderProps: any): any => (
              <EntityCompare {...renderProps} compareMode={'entity'} />
            )}
          />
          <PrivateRoute
            path={`/compare/entity/:entityType(${entityTypes})/:entityIds/:topic(${topicTypes})`}
            render={(renderProps: any): any => (
              <EntityCompare {...renderProps} compareMode={'entity'} />
            )}
          />
          <PrivateRoute
            path="/search/:searchTerm?"
            render={(renderProps: any): any => <Search {...renderProps} />}
          />
          <PrivateRoute
            path="/glossary/:glossaryTerm?"
            render={(renderProps: any): any => <Glossary {...renderProps} />}
          />
          <PrivateRoute
            exact
            path="/list/:id/invitation/:inviteId"
            render={(renderProps: any): any => (
              <CustomListInvitation {...renderProps} />
            )}
          />
          <PrivateRoute
            path="/list/:id"
            render={(renderProps: any): any => (
              <CustomList {...renderProps} topicFilters={topicFiltersState} />
            )}
          />
          <PrivateRoute
            exact
            path="/chart/:chartType"
            render={(renderProps: any): any => <Chart {...renderProps} />}
          />
          <PrivateRoute
            exact
            path="/playlistActivity"
            render={(renderProps: any): any => (
              <PlaylistActivity {...renderProps} />
            )}
          />
          <PrivateRoute
            exact
            path="/faqActivityTracker"
            render={(renderProps: any): any => (
              <FAQActivityTracker {...renderProps} />
            )}
          />
          <PrivateRoute
            exact
            path="/toplists"
            render={(renderProps: any): any => <TopLists {...renderProps} />}
          />
          <PrivateRoute
            exact
            path="/preunsubscribe"
            render={(): any => <PreUnsubscribedPage />}
          />
          <PrivateRoute exact path="/faq" render={(): any => <FAQ />} />
          <PrivateRoute
            exact
            path="/systemStatus"
            render={(): any => <SystemStatus />}
          />
          <PrivateRoute
            exact
            path="/trainingVideo"
            render={(): any => <TrainingVideo />}
          />
          <PrivateRoute exact path="/help" render={(): any => <Help />} />
          {this.state.showOpusNotifier && (
            <PrivateRoute
              exact
              path="/downloadOpusNotifierApp"
              render={(): any => <DownloadOpusNotifierAppPage />}
            />
          )}
          <Route
            render={(renderProps: any): any => (
              <ErrorPage
                {...renderProps}
                status={404}
                error={new Error('This page does not exist.')}
              />
            )}
          />
        </Switch>
        {isModalActive ? (
          <PrivateRoute
            path="/glossary/:glossaryTerm?"
            render={(renderProps: any): any => (
              <GlossaryModal
                previousLocation={this.previousLocation}
                {...renderProps}
                modalOptions={modalOptions}
              />
            )}
          />
        ) : null}
        {showUserProfileOnboarding && (
          <UserProfileOnboarding
            setState={(obj: any): any => {
              this.setState(obj);
            }}
          />
        )}
      </ErrorBoundary>
    );
  }
}

export default connect(
  (state: RootState): any => ({
    BetaGroupPriorityPlaylist:
      state.consulEnvData?.data?.BetaGroupPriorityPlaylist,
    topicFiltersState: state.topicFilters.value || {},
    authenticationProfile: state.authentication.profile,
    isExternalUser: state.authentication.isExternalUser,
    userHasVisitedVideoPage: state.userSettings.data?.userHasVisitedVideoPage,
    userLocation: state.userSettings.data?.userLocation,
    userDivision: state.userSettings.data?.userDivision,
    userRole: state.userSettings.data?.userRole,
    loadedAllUserSettings: state.userSettings.success?.all,
    isOpusNotifierOn: state.consulEnvData?.data?.isOpusNotifierOn,
  }),
  {
    shouldLoadTopicFilters,
    loadTopicFilters,
    shouldLoadUserSettings,
    loadConsulSettings,
    loadOrInitUserSettings,
  }
)(AppRoutes);
